import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { S3UploadError, uploadFileToS3 } from '../services/s3-upload';

export const MAX_UPLOAD_ATTEMPTS = 3;
const TRANSIENT_HTTP_STATUSES = [408, 429, 500, 502, 503, 504];

/**
 * Reports an upload that exhausted retries, including its actual attempt count.
 * Carries safe diagnostics for customer recovery handling and telemetry.
 */
export class RetryableImageUploadError extends Error {
  constructor(readonly attempts: number, readonly code?: string, readonly status?: number) {
    super('Card image upload failed after retries');
    this.name = 'RetryableImageUploadError';
  }
}

/**
 * Identifies normalized network/timeout failures or transient HTTP responses for retry.
 * Permanent HTTP failures and unclassified errors are not eligible.
 */
export const isTransientUploadError = (error: unknown): error is S3UploadError =>
  error instanceof S3UploadError && (error.status === undefined || TRANSIENT_HTTP_STATUSES.includes(error.status));

type Upload = { url: string; file: File; attempts: number };

/**
 * Provides S3 uploads with React Query-managed retries and backoff.
 * Each call reuses its signed URL and file for up to three total attempts.
 * Only the binary PUT is retried; asset linking and project saves remain with callers.
 *
 * @returns `{ uploadFile }`, a stable callback that resolves to `{ status }` or rejects when uploading fails.
 */
export const useS3Upload = () => {
  const { mutateAsync } = useMutation({
    mutationKey: ['s3-upload'],
    mutationFn: (upload: Upload) => {
      upload.attempts++;
      return uploadFileToS3(upload.url, upload.file);
    },
    // Retry only the idempotent PUT, not the subsequent link/save requests.
    retry: (failureCount, error) => failureCount < MAX_UPLOAD_ATTEMPTS - 1 && isTransientUploadError(error),
    // 500 ms then 1,000 ms, with +/-20% jitter.
    retryDelay: (failureCount) => 500 * 2 ** failureCount * (0.8 + Math.random() * 0.4),
    networkMode: 'always',
    gcTime: 0,
  });

  /**
   * Keeps attempt counts separate for concurrent uploads and reports exhausted retries.
   *
   * @param url Presigned S3 URL for the destination object.
   * @param file File to upload unchanged across attempts.
   * @returns Resolves to `{ status }`, the HTTP status S3 returned for the successful PUT.
   * @throws {RetryableImageUploadError} When transient failures exhaust the retry limit.
   * @throws The original error for permanent HTTP, invalid URL, or other non-retryable failures.
   */
  const uploadFile = useCallback(
    async (url: string, file: File) => {
      const upload: Upload = { url, file, attempts: 0 };
      try {
        return await mutateAsync(upload);
      } catch (error) {
        if (isTransientUploadError(error)) {
          throw new RetryableImageUploadError(upload.attempts, error.code, error.status);
        }
        throw error;
      }
    },
    [mutateAsync],
  );

  return { uploadFile };
};
